import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Building2,
  Calendar,
  DollarSign,
  Download,
  Loader2,
  RefreshCw,
  Target,
  Wallet,
} from 'lucide-react';
import {
  DollarsAtRiskPanel,
  AgingComposePanel,
} from '@saas-foundation/shared-frontend/financeView';
import {
  buildFinanceShareContext,
  useReportingSharePublisher,
} from '@saas-foundation/shared-frontend/reporting';
import { useDashboardBranding } from '../../pages/dashboard/hooks/useDashboardBranding';
import { executiveDashboardService } from '../../services/executiveDashboardService';
import { financeViewService } from '../../services/financeViewService';
import {
  REPORTING_PERIOD_PRESETS,
  formatReportingPeriodLabel,
  getDefaultReportingPeriod,
  normalizeReportingPeriod,
} from '../../utils/reportingPeriod';

const FINANCE_KPI_IDS = new Set([
  'net_collection_rate',
  'days_in_ar',
  'denial_rate',
  'observed_capture',
  'ehr_coverage',
  'remit_coverage',
]);

const REPORT_META = {
  'finance-view-summary': {
    title: 'Finance View Summary',
    description: 'Period export of platform finance KPIs — NCR, days in A/R, denials, and capture rates.',
  },
  'vs-ehr-variance': {
    title: 'vs EHR Variance Report',
    description: 'Capture confidence and book gaps between EHR, clearinghouse copies, and RCM posted cash.',
  },
  'capture-unmatched': {
    title: 'Capture & Unmatched Lists',
    description: 'EHR-only, clearinghouse-only, and unmatched claim keys for the selected period.',
  },
  'dollars-at-risk': {
    title: 'Dollars at Risk Report',
    description: 'Labeled underpayments, timely-filing risk, wrap (when enabled), and top CARC by dollar.',
  },
};

function formatKpiValue(kpi) {
  if (!kpi || kpi.value == null) return kpi?.integrationLabel || '—';
  const v = Number(kpi.value);
  if (Number.isNaN(v)) return String(kpi.value);
  if (kpi.unit === 'currency') return `$${v.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  if (kpi.unit === 'percent') return `${Number(v).toFixed(1)}%`;
  if (kpi.unit === 'days') return `${Number(v).toFixed(1)}d`;
  return v.toLocaleString();
}

function MetricTiles({ metrics = [] }) {
  if (!metrics.length) {
    return <p className="text-sm text-gray-500">No metrics available for this period.</p>;
  }
  return (
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
      {metrics.map((m) => (
        <div key={m.label} className="rounded-lg border border-gray-200 bg-white p-4">
          <p className="text-xs font-medium uppercase tracking-wide text-gray-500">{m.label}</p>
          <p className="mt-1 text-2xl font-bold text-gray-900">{m.value}</p>
          {m.note ? <p className="mt-1 text-xs text-gray-400">{m.note}</p> : null}
        </div>
      ))}
    </div>
  );
}

function downloadJson(payload, filename) {
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export default function FinanceReportPanel({ activeReport = 'finance-view-summary' }) {
  const view = REPORT_META[activeReport] ? activeReport : 'finance-view-summary';
  const meta = REPORT_META[view];
  const { resolvedOrgName, organizationId, orgContextLoading } = useDashboardBranding({ mode: 'finance' });
  const { publishShareContext, clearShareContext } = useReportingSharePublisher();

  const [period, setPeriod] = useState(() => getDefaultReportingPeriod());
  const [generating, setGenerating] = useState(false);
  const [message, setMessage] = useState(null);
  const [draft, setDraft] = useState(null);

  const periodLabel = useMemo(() => formatReportingPeriodLabel(period), [period]);

  useEffect(() => {
    setDraft(null);
    setMessage(null);
    clearShareContext();
  }, [view, clearShareContext]);

  useEffect(() => {
    if (!draft) {
      clearShareContext();
      return undefined;
    }
    publishShareContext(
      buildFinanceShareContext({
        reportName: meta.title,
        reportPeriod: periodLabel,
        metrics: draft.shareMetrics || [],
      }),
    );
    return () => clearShareContext();
  }, [draft, meta.title, periodLabel, publishShareContext, clearShareContext]);

  const handleGenerate = async () => {
    if (!organizationId) return;
    setGenerating(true);
    setMessage(null);
    try {
      const normalized = normalizeReportingPeriod(period);
      const [dashboard, atRisk, capture, unmatched] = await Promise.all([
        executiveDashboardService.getExecutiveDashboardData({
          period: normalized,
          organizationName: resolvedOrgName,
          organizationId,
          includeCustomMetrics: false,
        }),
        financeViewService.getDollarsAtRisk(organizationId),
        financeViewService.getCaptureConfidence(organizationId, normalized),
        financeViewService.getUnmatched(organizationId, normalized),
      ]);

      const financeKpis = (dashboard?.topKpis || []).filter(
        (kpi) => FINANCE_KPI_IDS.has(kpi.id) && !kpi.custom,
      );
      const aging = financeViewService.composeAging({ daysInArKpi: dashboard?.daysInArDetail });

      const shareMetrics = financeKpis
        .filter((kpi) => kpi.label)
        .map((kpi) => ({ label: kpi.label, value: formatKpiValue(kpi) }));

      if (atRisk) {
        const dollarParts = (atRisk.parts || []).filter(
          (p) => p.included && p.unit === 'usd' && p.amount != null,
        );
        const dollarTotal = dollarParts.reduce((sum, p) => sum + Number(p.amount || 0), 0);
        shareMetrics.push({
          label: 'Dollars at risk (USD parts)',
          value: `$${dollarTotal.toLocaleString(undefined, { maximumFractionDigits: 0 })}`,
        });
      }

      setDraft({
        generatedAt: new Date().toISOString(),
        period: periodLabel,
        dataStatus: dashboard?.dataStatus || 'unknown',
        financeKpis,
        aging,
        atRisk,
        capture,
        unmatched,
        shareMetrics,
      });
      setMessage({ type: 'success', text: 'Finance report generated.' });
    } catch (e) {
      setMessage({ type: 'error', text: e.message || 'Failed to generate finance report' });
    } finally {
      setGenerating(false);
    }
  };

  if (orgContextLoading) {
    return (
      <div className="flex flex-1 items-center justify-center py-20 text-gray-500">
        <Loader2 className="mr-2 h-6 w-6 animate-spin" />
        Loading organization…
      </div>
    );
  }

  if (!organizationId) {
    return (
      <div className="flex-1 p-8">
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900">
          Select an organization to generate Finance Reports.
        </div>
      </div>
    );
  }

  const captureMetrics = [];
  if (draft?.capture) {
    const c = draft.capture;
    for (const [key, label] of [
      ['ehr_coverage', 'EHR coverage'],
      ['remit_coverage', 'Remit coverage'],
      ['observed_capture', 'Observed capture'],
      ['ehrCoverage', 'EHR coverage'],
      ['remitCoverage', 'Remit coverage'],
      ['observedCapture', 'Observed capture'],
    ]) {
      if (c[key] != null && !captureMetrics.some((m) => m.label === label)) {
        const raw = c[key];
        const value = typeof raw === 'object' ? raw.rate ?? raw.value ?? raw.pct : raw;
        captureMetrics.push({
          label,
          value: typeof value === 'number' ? `${Number(value).toFixed(1)}%` : String(value),
          note: typeof raw === 'object' ? raw.denom_label || raw.note : undefined,
        });
      }
    }
  }

  const unmatchedRows = (() => {
    const u = draft?.unmatched;
    if (!u) return [];
    if (Array.isArray(u.rows)) return u.rows;
    if (Array.isArray(u.items)) return u.items;
    if (Array.isArray(u.unmatched)) return u.unmatched;
    const summary = [];
    if (u.ehr_only_count != null || u.ehrOnlyCount != null) {
      summary.push({ side: 'EHR-only', count: u.ehr_only_count ?? u.ehrOnlyCount });
    }
    if (u.ch_only_count != null || u.chOnlyCount != null) {
      summary.push({ side: 'Clearinghouse-only', count: u.ch_only_count ?? u.chOnlyCount });
    }
    if (u.both_incomplete_count != null) {
      summary.push({ side: 'Both incomplete', count: u.both_incomplete_count });
    }
    return summary;
  })();

  return (
    <div className="flex-1 overflow-y-auto bg-gray-50">
      <div className="mx-auto max-w-6xl space-y-6 p-6">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <h1 className="flex items-center gap-2 text-2xl font-bold text-gray-900">
              <Wallet className="h-7 w-7 text-emerald-600" />
              {meta.title}
            </h1>
            <p className="mt-1 text-sm text-gray-600">{meta.description}</p>
            <p className="mt-2 text-xs text-gray-500">
              Peer to Executive Reports — finance trust and variance, not leadership scorecards.{' '}
              <Link to="/dashboard/finance" className="font-medium text-emerald-700 hover:underline">
                Open Finance View →
              </Link>
            </p>
          </div>
          <div className="flex flex-wrap items-end gap-2">
            <label className="text-sm text-gray-600">
              Period
              <select
                value={period.preset}
                onChange={(e) => {
                  const preset = e.target.value;
                  if (preset === REPORTING_PERIOD_PRESETS.CUSTOM) {
                    setPeriod((prev) => ({ ...prev, preset }));
                    return;
                  }
                  setPeriod(normalizeReportingPeriod({ preset }));
                }}
                className="mt-1 block rounded-md border border-gray-300 px-2 py-1 text-sm"
              >
                <option value={REPORTING_PERIOD_PRESETS.LAST_30}>Last 30 days</option>
                <option value={REPORTING_PERIOD_PRESETS.LAST_90}>Last 90 days</option>
                <option value={REPORTING_PERIOD_PRESETS.LAST_180}>Last 180 days</option>
                <option value={REPORTING_PERIOD_PRESETS.CUSTOM}>Custom</option>
              </select>
            </label>
            {period.preset === REPORTING_PERIOD_PRESETS.CUSTOM && (
              <>
                <label className="text-sm text-gray-600">
                  From
                  <input
                    type="date"
                    value={period.startDate}
                    onChange={(e) => setPeriod((prev) => ({ ...prev, startDate: e.target.value, preset: REPORTING_PERIOD_PRESETS.CUSTOM }))}
                    className="mt-1 block rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
                <label className="text-sm text-gray-600">
                  To
                  <input
                    type="date"
                    value={period.endDate}
                    onChange={(e) => setPeriod((prev) => ({ ...prev, endDate: e.target.value, preset: REPORTING_PERIOD_PRESETS.CUSTOM }))}
                    className="mt-1 block rounded-md border border-gray-300 px-2 py-1 text-sm"
                  />
                </label>
              </>
            )}
            <button
              type="button"
              onClick={handleGenerate}
              disabled={generating}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm text-white hover:bg-emerald-700 disabled:opacity-50"
            >
              {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              Generate
            </button>
          </div>
        </div>

        {message && (
          <div
            className={`rounded-lg px-4 py-3 text-sm ${
              message.type === 'error'
                ? 'border border-red-200 bg-red-50 text-red-800'
                : 'border border-green-200 bg-green-50 text-green-800'
            }`}
          >
            {message.text}
          </div>
        )}

        {!draft && !generating && (
          <div className="rounded-lg border border-dashed border-gray-300 bg-white p-10 text-center text-gray-500">
            <DollarSign className="mx-auto mb-3 h-10 w-10 text-gray-400" />
            <p>Click Generate to compose this Finance Report from live platform books.</p>
          </div>
        )}

        {draft && (
          <div className="space-y-6 rounded-lg border border-gray-200 bg-white p-6 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3 text-sm text-gray-600">
              <span className="inline-flex items-center gap-1">
                <Calendar className="h-4 w-4" />
                Period: {draft.period}
              </span>
              <span>Generated: {new Date(draft.generatedAt).toLocaleString()}</span>
              <span>Status: {draft.dataStatus}</span>
              <button
                type="button"
                onClick={() =>
                  downloadJson(
                    { report: view, ...draft },
                    `${view}-${draft.period.replace(/\s+/g, '-').toLowerCase()}.json`,
                  )
                }
                className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-1.5 text-sm hover:bg-gray-50"
              >
                <Download className="h-4 w-4" />
                Download JSON
              </button>
            </div>

            {(view === 'finance-view-summary' || view === 'vs-ehr-variance') && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Target className="h-4 w-4 text-emerald-600" />
                  Finance KPIs
                </h3>
                <MetricTiles
                  metrics={draft.financeKpis.map((kpi) => ({
                    label: kpi.label,
                    value: formatKpiValue(kpi),
                    note: kpi.caption || kpi.sourceLabel,
                  }))}
                />
              </section>
            )}

            {(view === 'vs-ehr-variance' || view === 'capture-unmatched') && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <Building2 className="h-4 w-4 text-emerald-600" />
                  Capture confidence
                </h3>
                {captureMetrics.length ? (
                  <MetricTiles metrics={captureMetrics} />
                ) : (
                  <p className="text-sm text-gray-500">
                    Capture confidence is unavailable until Gold finance datasets are connected for this org.
                  </p>
                )}
              </section>
            )}

            {(view === 'capture-unmatched' || view === 'vs-ehr-variance') && (
              <section className="space-y-3">
                <h3 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
                  <AlertTriangle className="h-4 w-4 text-amber-600" />
                  Unmatched / orphan signals
                </h3>
                {unmatchedRows.length === 0 ? (
                  <p className="text-sm text-gray-500">
                    No unmatched rows returned for this period. Connect EHR and clearinghouse feeds to populate lists.
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="min-w-full overflow-hidden rounded-lg border border-gray-200 text-sm">
                      <thead className="bg-gray-50 text-left text-xs uppercase text-gray-500">
                        <tr>
                          <th className="px-3 py-2">Side / key</th>
                          <th className="px-3 py-2 text-right">Count / amount</th>
                        </tr>
                      </thead>
                      <tbody>
                        {unmatchedRows.slice(0, 25).map((row, idx) => (
                          <tr key={row.id || row.claim_key || row.side || idx} className="border-t border-gray-100">
                            <td className="px-3 py-2">
                              {row.side || row.claim_key || row.key || row.label || 'Row'}
                              {row.payer ? (
                                <span className="block text-xs text-gray-400">{row.payer}</span>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 text-right">
                              {row.count != null
                                ? row.count
                                : row.amount != null
                                  ? `$${Number(row.amount).toLocaleString()}`
                                  : row.status || '—'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </section>
            )}

            {(view === 'dollars-at-risk' || view === 'finance-view-summary') && (
              <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
                <DollarsAtRiskPanel atRisk={draft.atRisk} />
                <AgingComposePanel aging={draft.aging} />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
